import type {
  BatchRow,
  DashboardKpis,
  LedgerEntry,
  Paged,
  PayableRow,
  QuotationPipelineSummary,
  QuotationRow,
  ReceivableRow,
  ReportExportRequest,
  SaleDetail,
  SalesByCategoryRow,
  SalesByDocTypeRow,
  SalesByPaymentMethodRow,
  SalesByPeriodRow,
  SalesByProductRow,
  SalesHistoryRow,
  ShiftRow,
  StockMovementRow,
  StockOnHandRow,
  TaxBreakdownRow,
} from '@sellokart/shared';
import { API_URL, portalApi, queryString } from '@/lib/api';
import { tenantSession } from '@/lib/session';
import type { ReportFilters } from './useReportFilters';

export const reportKeys = {
  all: ['reports'] as const,
  dashboard: (filters: ReportFilters) => ['reports', 'dashboard', filters] as const,
  salesTrend: (filters: ReportFilters, granularity: 'day' | 'month') =>
    ['reports', 'sales', 'trend', filters, granularity] as const,
  salesByProduct: (filters: ReportFilters) => ['reports', 'sales', 'by-product', filters] as const,
  salesByCategory: (filters: ReportFilters) => ['reports', 'sales', 'by-category', filters] as const,
  salesByPaymentMethod: (filters: ReportFilters) => ['reports', 'sales', 'by-payment-method', filters] as const,
  salesByDocType: (filters: ReportFilters) => ['reports', 'sales', 'by-doc-type', filters] as const,
  shifts: (filters: ReportFilters) => ['reports', 'shifts', filters] as const,
  ledger: (filters: ReportFilters) => ['reports', 'ledger', filters] as const,
  receivables: (branchId: string) => ['reports', 'receivables', branchId] as const,
  payables: (branchId: string) => ['reports', 'payables', branchId] as const,
  salesHistory: (filters: ReportFilters, page: number, pageSize: number) =>
    ['reports', 'sales-history', filters, page, pageSize] as const,
  saleDetail: (deviceId: string, sourceId: number) => ['reports', 'sales-history', deviceId, sourceId] as const,
  tax: (filters: ReportFilters) => ['reports', 'tax', filters] as const,
  inventoryBatches: (filters: ReportFilters, page: number, pageSize: number) =>
    ['reports', 'inventory', 'batches', filters, page, pageSize] as const,
  inventoryStock: (filters: ReportFilters, page: number, pageSize: number) =>
    ['reports', 'inventory', 'stock', filters, page, pageSize] as const,
  inventoryMovement: (filters: ReportFilters, page: number, pageSize: number) =>
    ['reports', 'inventory', 'movement', filters, page, pageSize] as const,
  quotations: (filters: ReportFilters) => ['reports', 'quotations', filters] as const,
  quotationsList: (filters: ReportFilters) => ['reports', 'quotations', 'list', filters] as const,
};

/** Query string shared by every report endpoint's filter set, plus whatever the caller adds. */
function filterQuery(filters: ReportFilters, extra: Record<string, string | number> = {}): string {
  return queryString({
    branchId: filters.branchId,
    from: filters.from,
    to: filters.to,
    docType: filters.docType,
    search: filters.search,
    filters: filters.filters,
    matchAny: filters.matchAny,
    ...extra,
  });
}

export function fetchDashboard(filters: ReportFilters, signal?: AbortSignal): Promise<DashboardKpis> {
  return portalApi.get<DashboardKpis>(`/portal/reports/dashboard${filterQuery(filters)}`, { signal });
}

export function fetchSalesTrend(
  filters: ReportFilters,
  granularity: 'day' | 'month',
  signal?: AbortSignal,
): Promise<SalesByPeriodRow[]> {
  return portalApi.get<SalesByPeriodRow[]>(
    `/portal/reports/sales/trend${filterQuery(filters, { granularity })}`,
    { signal },
  );
}

export function fetchSalesByProduct(filters: ReportFilters, signal?: AbortSignal): Promise<SalesByProductRow[]> {
  return portalApi.get<SalesByProductRow[]>(`/portal/reports/sales/by-product${filterQuery(filters)}`, { signal });
}

export function fetchSalesByCategory(filters: ReportFilters, signal?: AbortSignal): Promise<SalesByCategoryRow[]> {
  return portalApi.get<SalesByCategoryRow[]>(`/portal/reports/sales/by-category${filterQuery(filters)}`, { signal });
}

export function fetchSalesByPaymentMethod(
  filters: ReportFilters,
  signal?: AbortSignal,
): Promise<SalesByPaymentMethodRow[]> {
  return portalApi.get<SalesByPaymentMethodRow[]>(
    `/portal/reports/sales/by-payment-method${filterQuery(filters)}`,
    { signal },
  );
}

/** `by-doc-type` doesn't accept `docType` server-side (it's the split BY doc type), but the extra query param is harmless. */
export function fetchSalesByDocType(filters: ReportFilters, signal?: AbortSignal): Promise<SalesByDocTypeRow[]> {
  return portalApi.get<SalesByDocTypeRow[]>(`/portal/reports/sales/by-doc-type${filterQuery(filters)}`, { signal });
}

export function fetchShifts(filters: ReportFilters, signal?: AbortSignal): Promise<ShiftRow[]> {
  return portalApi.get<ShiftRow[]>(`/portal/reports/shifts${filterQuery(filters)}`, { signal });
}

export function fetchLedger(filters: ReportFilters, signal?: AbortSignal): Promise<LedgerEntry[]> {
  return portalApi.get<LedgerEntry[]>(`/portal/reports/ledger${filterQuery(filters)}`, { signal });
}

/** Point-in-time balance — no date range, only an optional branch filter. */
export function fetchReceivables(branchId: string, signal?: AbortSignal): Promise<ReceivableRow[]> {
  return portalApi.get<ReceivableRow[]>(`/portal/reports/receivables${queryString({ branchId })}`, { signal });
}

/** Point-in-time balance — no date range, only an optional branch filter. */
export function fetchPayables(branchId: string, signal?: AbortSignal): Promise<PayableRow[]> {
  return portalApi.get<PayableRow[]>(`/portal/reports/payables${queryString({ branchId })}`, { signal });
}

export function fetchSalesHistory(
  filters: ReportFilters,
  page: number,
  pageSize: number,
  signal?: AbortSignal,
): Promise<Paged<SalesHistoryRow>> {
  return portalApi.get<Paged<SalesHistoryRow>>(
    `/portal/reports/sales-history${filterQuery(filters, { page, pageSize })}`,
    { signal },
  );
}

/** Direct lookup by the sale's compound key — no filters at all. */
export function fetchSaleDetail(deviceId: string, sourceId: number, signal?: AbortSignal): Promise<SaleDetail> {
  return portalApi.get<SaleDetail>(`/portal/reports/sales-history/${deviceId}/${sourceId}`, { signal });
}

export function fetchTax(filters: ReportFilters, signal?: AbortSignal): Promise<TaxBreakdownRow[]> {
  return portalApi.get<TaxBreakdownRow[]>(`/portal/reports/tax${filterQuery(filters)}`, { signal });
}

/** Point-in-time stock on hand — no date range, only an optional branch filter — but paginated server-side like sales history, since a tenant's batches can outgrow one page just as easily. */
export function fetchInventoryBatches(
  filters: ReportFilters,
  page: number,
  pageSize: number,
  signal?: AbortSignal,
): Promise<Paged<BatchRow>> {
  return portalApi.get<Paged<BatchRow>>(
    `/portal/reports/inventory/batches${filterQuery(filters, { page, pageSize })}`,
    { signal },
  );
}

export function fetchInventoryStock(
  filters: ReportFilters,
  page: number,
  pageSize: number,
  signal?: AbortSignal,
): Promise<Paged<StockOnHandRow>> {
  return portalApi.get<Paged<StockOnHandRow>>(
    `/portal/reports/inventory/stock${filterQuery(filters, { page, pageSize })}`,
    { signal },
  );
}

export function fetchInventoryMovement(
  filters: ReportFilters,
  page: number,
  pageSize: number,
  signal?: AbortSignal,
): Promise<Paged<StockMovementRow>> {
  return portalApi.get<Paged<StockMovementRow>>(
    `/portal/reports/inventory/movement${filterQuery(filters, { page, pageSize })}`,
    { signal },
  );
}

export function fetchQuotations(filters: ReportFilters, signal?: AbortSignal): Promise<QuotationPipelineSummary> {
  return portalApi.get<QuotationPipelineSummary>(`/portal/reports/quotations${filterQuery(filters)}`, { signal });
}

export function fetchQuotationsList(filters: ReportFilters, signal?: AbortSignal): Promise<QuotationRow[]> {
  return portalApi.get<QuotationRow[]>(`/portal/reports/quotations/list${filterQuery(filters)}`, { signal });
}

export type InventoryExportReport = 'batches' | 'stock' | 'movement';
export type ExportFormat = 'pdf' | 'xlsx';

/**
 * Fetch a generated Inventory export and save it.
 *
 * Deliberately NOT a plain `<a download>`: the portal authenticates with a
 * bearer token rather than a cookie, so a link would arrive unauthenticated
 * and the browser would cheerfully save the 401 body under a .xlsx name.
 * Fetching with the token and saving the blob is the only way the file is
 * the file.
 *
 * The filename comes from Content-Disposition so the server stays the
 * single authority on naming — it already knows the shop and the range.
 */
export async function downloadInventoryExport(
  report: InventoryExportReport,
  format: ExportFormat,
  filters: ReportFilters,
): Promise<void> {
  const token = tenantSession.getAccessToken();
  if (token === null) throw new Error('Your session has expired. Sign in again.');

  const response = await fetch(
    `${API_URL}/portal/reports/inventory/${report}/export.${format}${filterQuery(filters)}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  await saveExportResponse(response, `${report}.${format}`);
}

/**
 * Export the table a report page is showing (the 13 pages without an export
 * of their own). The page sends its rows as display text; the server renders
 * them with the shop's name and currency. Same bearer-token download as
 * {@link downloadInventoryExport}.
 */
export async function downloadReportExport(
  table: ReportExportRequest,
  format: ExportFormat,
): Promise<void> {
  const token = tenantSession.getAccessToken();
  if (token === null) throw new Error('Your session has expired. Sign in again.');

  const response = await fetch(`${API_URL}/portal/reports/export.${format}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(table),
  });
  await saveExportResponse(response, `report.${format}`);
}

async function saveExportResponse(response: Response, fallbackName: string): Promise<void> {
  if (!response.ok) {
    // The error body is JSON even though the request asked for a file.
    let detail = `Export failed (${response.status}).`;
    try {
      const body = (await response.json()) as { message?: string | string[] };
      const message = Array.isArray(body.message) ? body.message.join('; ') : body.message;
      if (message) detail = message;
    } catch {
      // Not JSON — a proxy error page. The status is all we can say.
    }
    throw new Error(detail);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filenameFrom(response.headers.get('content-disposition')) ?? fallbackName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Revoked on the next tick: revoking synchronously can cancel the download
  // in some browsers before it has read the blob.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** `attachment; filename="batches-expiry_shop_2026-07-01_2026-07-31.xlsx"` */
function filenameFrom(header: string | null): string | null {
  if (!header) return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  return match ? decodeURIComponent(match[1]) : null;
}

export { filterQuery };
